import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Sparkles, BookOpen, GraduationCap, Mic, Send, ArrowLeft, AlertCircle, RefreshCw, Volume2, MessageSquare, Lightbulb, Cpu, Trash2, Play } from '../../components/icons';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { supabase } from '../../lib/supabase';
import {
  checkCoachNLPPracticeAccess,
  getCoachNLPPracticePartnerId,
  fetchFirstEnabledNLPPracticeConfig,
  createNLPSession,
  completeNLPSession,
  fetchCoachSessions,
  fetchSessionMessages,
  deleteNLPSession,
  sendNLPPracticeMessage,
  type NLPModelVariant,
  type NLPPracticeSession,
} from '../../lib/nlp-practice';
import { isUserAdmin } from '../../lib/auth';
import {
  NLP_MODULES,
  NLP_PRACTICE_STYLES,
  NLP_DIFFICULTIES,
  DEFAULT_META_MODEL_INSTRUCTIONS,
  DEFAULT_PRESUPPOSITIONS_INSTRUCTIONS,
  DEFAULT_MILTON_MODEL_INSTRUCTIONS,
  MILTON_PRACTICE_TYPES,
  MILTON_PATTERN_FOCUSES,
  MILTON_CONTEXTS,
  MILTON_FEEDBACK_TIMINGS,
  MILTON_DURATIONS,
} from '../../components/ai/nlpPracticeConstants';
import type { NLPModule, NLPPracticeStyle, NLPDifficulty, NLPSessionMode, MiltonPatternFocus, MiltonContext, MiltonFeedbackTiming, MiltonDuration } from '../../components/ai/nlpPracticeConstants';
import { NLPPracticeVoice } from '../../components/ai/NLPPracticeVoice';
import { MarkdownRenderer } from '../../components/ai/MarkdownRenderer';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

type Step = 'select_module' | 'select_style' | 'select_difficulty' | 'select_milton_setup' | 'practice' | 'history' | 'trainer_chat' | 'view_session';

export function NLPPracticePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('select_module');
  const [selectedModule, setSelectedModule] = useState<NLPModule | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<NLPPracticeStyle | null>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<NLPDifficulty | null>(null);
  const [practiceMode, setPracticeMode] = useState<NLPSessionMode>('text');
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionStartTime, setSessionStartTime] = useState<number>(0);
  const [error, setError] = useState('');
  const [sessions, setSessions] = useState<NLPPracticeSession[]>([]);
  const [viewingSession, setViewingSession] = useState<NLPPracticeSession | null>(null);
  const [viewingMessages, setViewingMessages] = useState<ChatMessage[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<NLPPracticeSession | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [modelVariant, setModelVariant] = useState<NLPModelVariant>('gpt-realtime-2.1');
  const [miltonPracticeType, setMiltonPracticeType] = useState<NLPPracticeStyle | null>(null);
  const [miltonDifficulty, setMiltonDifficulty] = useState<NLPDifficulty>('beginner');
  const [miltonPatternFocus, setMiltonPatternFocus] = useState<MiltonPatternFocus>('full_mixed');
  const [miltonContext, setMiltonContext] = useState<MiltonContext>('coaching');
  const [miltonFeedbackTiming, setMiltonFeedbackTiming] = useState<MiltonFeedbackTiming>('every_answer');
  const [miltonDuration, setMiltonDuration] = useState<MiltonDuration>(10);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const admin = await isUserAdmin(user.id);
      setIsAdmin(admin);

      const { data: coach } = await supabase
        .from('coaches')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      let coachAuthorized = false;

      if (coach) {
        setCoachId(coach.id);
        coachAuthorized = await checkCoachNLPPracticeAccess();
        setAuthorized(coachAuthorized);
        if (coachAuthorized) {
          const pid = await getCoachNLPPracticePartnerId();
          setPartnerId(pid);
          const history = await fetchCoachSessions(coach.id);
          setSessions(history);
        }
      }

      // Admin bypass: if no coach record or not authorized, but user is admin,
      // grant access using the first enabled NLP practice config
      if ((!coach || !coachAuthorized) && admin) {
        const config = await fetchFirstEnabledNLPPracticeConfig();
        if (config) {
          setAuthorized(true);
          setPartnerId(config.partner_id);
        }
      }

      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startTrainerChat = useCallback(async () => {
    if (!partnerId) return;
    const id = await createNLPSession(coachId, partnerId, 'nlp_trainer', 'ask', null, 'text');
    setSessionId(id);
    setSessionStartTime(Date.now());
    setMessages([]);
    setStep('trainer_chat');
  }, [coachId, partnerId]);

  const startSession = useCallback(async () => {
    if (!partnerId || !selectedModule || !selectedStyle) return;
    const miltonOptions = selectedModule === 'milton_model' ? {
      patternFocus: miltonPatternFocus,
      practiceContext: miltonContext,
      feedbackTiming: miltonFeedbackTiming,
      targetDurationMinutes: miltonDuration,
    } : undefined;
    const id = await createNLPSession(coachId, partnerId, selectedModule, selectedStyle, selectedDifficulty, practiceMode, miltonOptions);
    setSessionId(id);
    setSessionStartTime(Date.now());
    setMessages([]);
    setStep('practice');
  }, [coachId, partnerId, selectedModule, selectedStyle, selectedDifficulty, practiceMode, miltonPatternFocus, miltonContext, miltonFeedbackTiming, miltonDuration]);

  const handleSend = useCallback(async () => {
    if (!chatInput.trim() || isSending || !selectedModule) return;
    const effectiveStyle = selectedModule === 'nlp_trainer' ? 'ask' : selectedStyle;
    if (!effectiveStyle) return;

    const userMsg: ChatMessage = { role: 'user', content: chatInput.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setChatInput('');
    setIsSending(true);
    setError('');

    try {
      const miltonOptions = selectedModule === 'milton_model' ? {
        patternFocus: miltonPatternFocus,
        practiceContext: miltonContext,
        feedbackTiming: miltonFeedbackTiming,
        targetDurationMinutes: miltonDuration,
      } : undefined;
      const result = await sendNLPPracticeMessage(
        userMsg.content,
        messages,
        selectedModule,
        effectiveStyle,
        selectedDifficulty,
        sessionId,
        miltonOptions,
      );
      setMessages([...newMessages, { role: 'assistant', content: result.response }]);
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
      setMessages(newMessages);
    } finally {
      setIsSending(false);
    }
  }, [chatInput, isSending, messages, selectedModule, selectedStyle, selectedDifficulty, sessionId, miltonPatternFocus, miltonContext, miltonFeedbackTiming, miltonDuration]);

  const handleSendTrainer = useCallback(async () => {
    if (!chatInput.trim() || isSending) return;

    const userMsg: ChatMessage = { role: 'user', content: chatInput.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setChatInput('');
    setIsSending(true);
    setError('');

    try {
      const result = await sendNLPPracticeMessage(
        userMsg.content,
        messages,
        'nlp_trainer',
        'ask',
        null,
        sessionId,
      );
      setMessages([...newMessages, { role: 'assistant', content: result.response }]);
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
      setMessages(newMessages);
    } finally {
      setIsSending(false);
    }
  }, [chatInput, isSending, messages, sessionId]);

  const handleEndSession = useCallback(async (voiceDuration?: number) => {
    const duration = voiceDuration || Math.floor((Date.now() - sessionStartTime) / 1000);

    // Ask AI for feedback summary (skip for nlp_trainer — it's Q&A, not scored practice)
    let score: number | null = null;
    let feedback: string | null = null;

    if (messages.length > 0 && selectedModule && selectedStyle && selectedModule !== 'nlp_trainer') {
      try {
        const miltonOptions = selectedModule === 'milton_model' ? {
          patternFocus: miltonPatternFocus,
          practiceContext: miltonContext,
          feedbackTiming: miltonFeedbackTiming,
          targetDurationMinutes: miltonDuration,
        } : undefined;
        const feedbackResponse = await sendNLPPracticeMessage(
          selectedModule === 'milton_model'
            ? 'Please provide my Milton Model practice report now. Give me a score out of 10 using the scoring rubric and structured feedback based on this session.'
            : 'Please provide my practice report now. Give me a score out of 100 and structured feedback based on this session.',
          messages,
          selectedModule,
          selectedStyle,
          selectedDifficulty,
          sessionId,
          miltonOptions,
        );
        feedback = feedbackResponse.response;

        const scorePatterns = selectedModule === 'milton_model'
          ? [/(\d+)\s*\/\s*10/, /score[^\d]*(\d+)\s*(?:\/|out of)\s*10/i, /rating[^\d]*(\d+)/i]
          : [/(\d+)\s*\/\s*100/, /score[^\d]*(\d+)\s*(?:\/|out of)\s*100/i, /rating[^\d]*(\d+)/i];
        for (const pattern of scorePatterns) {
          const match = feedback.match(pattern);
          if (match) {
            const parsed = parseInt(match[1]);
            if (!isNaN(parsed) && parsed >= 0 && parsed <= (selectedModule === 'milton_model' ? 10 : 100)) {
              score = parsed;
              break;
            }
          }
        }
      } catch {
        // feedback is optional
      }
    }

    if (sessionId && duration > 0) {
      await completeNLPSession(sessionId, duration, score, feedback);
    }

    if (coachId) {
      const history = await fetchCoachSessions(coachId);
      setSessions(history);
    }

    setStep('select_module');
    setSelectedModule(null);
    setSelectedStyle(null);
    setSelectedDifficulty(null);
    setMiltonPracticeType(null);
    setMessages([]);
    setSessionId(null);
    setVoiceOpen(false);
  }, [messages, selectedModule, selectedStyle, selectedDifficulty, sessionId, sessionStartTime, coachId, miltonPatternFocus, miltonContext, miltonFeedbackTiming, miltonDuration]);

  const handleViewSession = useCallback(async (session: NLPPracticeSession) => {
    setViewingSession(session);
    setViewingMessages([]);
    setIsLoadingMessages(true);
    setStep('view_session');
    try {
      const msgs = await fetchSessionMessages(session.id);
      setViewingMessages(msgs.map(m => ({ role: m.role as 'user' | 'assistant', content: m.content })));
    } catch (err) {
      console.error('Error loading session messages:', err);
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  const handleResumeSession = useCallback(async (session: NLPPracticeSession) => {
    setIsLoadingMessages(true);
    try {
      const msgs = await fetchSessionMessages(session.id);
      setMessages(msgs.map(m => ({ role: m.role as 'user' | 'assistant', content: m.content })));
      setSessionId(session.id);
      setSelectedModule(session.module);
      setSelectedStyle(session.practice_style);
      setSelectedDifficulty(session.difficulty);
      setPracticeMode(session.mode);
      setSessionStartTime(Date.now());

      if (session.module === 'nlp_trainer') {
        setStep('trainer_chat');
      } else {
        setStep('practice');
      }
    } catch (err) {
      console.error('Error resuming session:', err);
      setError('Failed to resume session. Please try again.');
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  const handleDeleteSession = useCallback(async () => {
    if (!sessionToDelete) return;
    setIsDeleting(true);
    try {
      await deleteNLPSession(sessionToDelete.id);
      setSessions(prev => prev.filter(s => s.id !== sessionToDelete.id));
      setSessionToDelete(null);
    } catch (err) {
      console.error('Error deleting session:', err);
    } finally {
      setIsDeleting(false);
    }
  }, [sessionToDelete]);

  const handleEndTrainerChat = useCallback(async () => {
    const duration = Math.floor((Date.now() - sessionStartTime) / 1000);
    if (sessionId && duration > 0) {
      await completeNLPSession(sessionId, duration, null, null);
    }
    if (coachId) {
      const history = await fetchCoachSessions(coachId);
      setSessions(history);
    }
    setStep('select_module');
    setSelectedModule(null);
    setSelectedStyle(null);
    setMessages([]);
    setSessionId(null);
  }, [sessionId, sessionStartTime, coachId]);

  if (loading) return <LoadingSpinner message="Loading NLP Practice..." />;

  if (!authorized) {
    return (
      <div className="min-h-screen freud-bg py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="freud-card-elevated p-8">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h1 className="freud-heading-xl text-chocolate-900 mb-4">NLP Practice Not Available</h1>
            <p className="text-chocolate-500 mb-6">
              You do not currently have access to the NLP Skills Practice Coach. Please contact your trainer or partner to request access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-sage-100 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-sage-600" />
              </div>
              <div>
                <h1 className="freud-heading-xl text-chocolate-900">NLP Skills Practice</h1>
                <p className="text-sm text-chocolate-500">Practice Meta Model and Presuppositions with your AI coach</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/feedback')}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 rounded-xl hover:bg-amber-100 transition-colors"
              >
                <Lightbulb className="w-4 h-4" />
                Send Feedback
              </button>
              <button
                onClick={() => setStep('history')}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-sage-700 bg-sage-50 rounded-xl hover:bg-sage-100 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                Practice History
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {step === 'select_module' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h2 className="text-lg font-bold text-chocolate-900 mb-2">What would you like to practice?</h2>
            <p className="text-sm text-chocolate-500 mb-6">Choose a module to begin your practice session.</p>
            <div className="grid md:grid-cols-2 gap-4">
              {NLP_MODULES.map((mod) => (
                <button
                  key={mod.id}
                  onClick={() => {
                    setSelectedModule(mod.id);
                    setSelectedDifficulty(null);
                    if (mod.id === 'nlp_trainer') {
                      setSelectedStyle('ask');
                      setPracticeMode('text');
                      startTrainerChat();
                    } else if (mod.id === 'meta_model') {
                      setStep('select_difficulty');
                    } else if (mod.id === 'milton_model') {
                      setStep('select_milton_setup');
                    } else {
                      setStep('select_style');
                    }
                  }}
                  className="freud-card-elevated p-6 text-left hover:ring-2 hover:ring-sage-400 transition-all"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
                      <Brain className="w-5 h-5 text-sage-600" />
                    </div>
                    <h3 className="text-lg font-bold text-chocolate-900">{mod.label}</h3>
                  </div>
                  <p className="text-sm text-chocolate-500">{mod.description}</p>
                </button>
              ))}
              <button
                onClick={() => navigate('/coach/reframing-practice')}
                className="freud-card-elevated p-6 text-left hover:ring-2 hover:ring-sage-400 transition-all border border-sage-200"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
                    <Brain className="w-5 h-5 text-sage-600" />
                  </div>
                  <h3 className="text-lg font-bold text-chocolate-900">Reframing AI Coach</h3>
                </div>
                <p className="text-sm text-chocolate-500">Practise identifying and creating context reframes, meaning reframes and alternative behaviours while receiving personalised AI feedback.</p>
              </button>
            </div>

            {sessions.length > 0 && (
              <div className="mt-8">
                <h3 className="text-sm font-bold text-chocolate-900 mb-3 uppercase tracking-wide">Recent Practice</h3>
                <div className="space-y-2">
                  {sessions.slice(0, 5).map((s) => (
                    <div
                      key={s.id}
                      onClick={() => handleViewSession(s)}
                      className="freud-card-elevated p-4 flex items-center justify-between cursor-pointer hover:ring-2 hover:ring-sage-300 transition-all group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-sage-50 rounded-lg flex items-center justify-center">
                          <span className="text-xs font-bold text-sage-700">
                            {s.module === 'meta_model' ? 'MM' : s.module === 'presuppositions' ? 'PS' : s.module === 'milton_model' ? 'ML' : 'NT'}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-chocolate-800">
                            {s.module === 'meta_model' ? 'Meta Model' : s.module === 'presuppositions' ? 'Presuppositions' : s.module === 'milton_model' ? 'Milton Model' : 'NLP Trainer AI'} — {s.practice_style}
                          </p>
                          <p className="text-xs text-chocolate-400">
                            {new Date(s.created_at).toLocaleDateString()} · {s.status === 'in_progress' ? 'In progress' : s.score !== null ? `${s.score}${s.module === 'milton_model' ? '/10' : '/100'}` : 'Completed'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {s.status === 'in_progress' && (
                          <span
                            onClick={(e) => { e.stopPropagation(); handleResumeSession(s); }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-sage-50 text-sage-700 rounded-lg text-xs font-semibold hover:bg-sage-100 transition-colors"
                          >
                            <Play className="w-3 h-3" />
                            Resume
                          </span>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); setSessionToDelete(s); }}
                          className="p-2 text-chocolate-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all"
                          title="Delete session"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {step === 'select_difficulty' && selectedModule === 'meta_model' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button
              onClick={() => setStep('select_module')}
              className="flex items-center gap-2 text-chocolate-500 hover:text-sage-600 mb-4 transition-colors text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <h2 className="text-lg font-bold text-chocolate-900 mb-2">Choose your difficulty</h2>
            <p className="text-sm text-chocolate-500 mb-6">Select the level that matches your current skill.</p>
            <div className="grid md:grid-cols-3 gap-4">
              {NLP_DIFFICULTIES.map((diff) => (
                <button
                  key={diff.id}
                  onClick={() => {
                    setSelectedDifficulty(diff.id);
                    setStep('select_style');
                  }}
                  className="freud-card-elevated p-5 text-left hover:ring-2 hover:ring-sage-400 transition-all"
                >
                  <h3 className="text-base font-bold text-chocolate-900 mb-1">{diff.label}</h3>
                  <p className="text-xs text-chocolate-500">{diff.description}</p>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {step === 'select_milton_setup' && selectedModule === 'milton_model' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button
              onClick={() => setStep('select_module')}
              className="flex items-center gap-2 text-chocolate-500 hover:text-sage-600 mb-4 transition-colors text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <h2 className="text-lg font-bold text-chocolate-900 mb-2">Milton Model Practice Setup</h2>
            <p className="text-sm text-chocolate-500 mb-6">Configure your practice session.</p>

            <div className="space-y-5">
              <div>
                <h3 className="text-sm font-bold text-chocolate-900 mb-2">Practice Type</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {MILTON_PRACTICE_TYPES.map((pt) => (
                    <button
                      key={pt.id}
                      onClick={() => {
                        setMiltonPracticeType(pt.id);
                        setSelectedStyle(pt.id);
                      }}
                      className={`freud-card-elevated p-4 text-left transition-all ${
                        miltonPracticeType === pt.id ? 'ring-2 ring-sage-500' : 'hover:ring-2 hover:ring-sage-400'
                      }`}
                    >
                      <h4 className="text-sm font-bold text-chocolate-900 mb-1">{pt.label}</h4>
                      <p className="text-xs text-chocolate-500">{pt.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-chocolate-900 mb-2">Level</h3>
                <div className="grid grid-cols-3 gap-3">
                  {NLP_DIFFICULTIES.map((diff) => (
                    <button
                      key={diff.id}
                      onClick={() => { setMiltonDifficulty(diff.id); setSelectedDifficulty(diff.id); }}
                      className={`px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                        miltonDifficulty === diff.id ? 'bg-sage-600 text-white' : 'bg-sage-50 text-sage-700 hover:bg-sage-100'
                      }`}
                    >
                      {diff.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-chocolate-900 mb-2">Pattern Focus</h3>
                <div className="flex flex-wrap gap-2">
                  {MILTON_PATTERN_FOCUSES.map((pf) => (
                    <button
                      key={pf.id}
                      onClick={() => setMiltonPatternFocus(pf.id)}
                      className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        miltonPatternFocus === pf.id ? 'bg-sage-600 text-white' : 'bg-sage-50 text-sage-700 hover:bg-sage-100'
                      }`}
                    >
                      {pf.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-chocolate-900 mb-2">Context</h3>
                <div className="flex flex-wrap gap-2">
                  {MILTON_CONTEXTS.map((ctx) => (
                    <button
                      key={ctx.id}
                      onClick={() => setMiltonContext(ctx.id)}
                      className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        miltonContext === ctx.id ? 'bg-sage-600 text-white' : 'bg-sage-50 text-sage-700 hover:bg-sage-100'
                      }`}
                    >
                      {ctx.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-chocolate-900 mb-2">Feedback</h3>
                <div className="flex flex-wrap gap-2">
                  {MILTON_FEEDBACK_TIMINGS.map((ft) => (
                    <button
                      key={ft.id}
                      onClick={() => setMiltonFeedbackTiming(ft.id)}
                      className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        miltonFeedbackTiming === ft.id ? 'bg-sage-600 text-white' : 'bg-sage-50 text-sage-700 hover:bg-sage-100'
                      }`}
                    >
                      {ft.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-chocolate-900 mb-2">Session Duration</h3>
                <div className="flex flex-wrap gap-2">
                  {MILTON_DURATIONS.map((d) => (
                    <button
                      key={d.id}
                      onClick={() => setMiltonDuration(d.id)}
                      className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        miltonDuration === d.id ? 'bg-sage-600 text-white' : 'bg-sage-50 text-sage-700 hover:bg-sage-100'
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              {miltonPracticeType && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="freud-card-elevated p-5"
                >
                  <h3 className="text-sm font-bold text-chocolate-900 mb-3">Choose your practice mode</h3>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <button
                      onClick={() => setPracticeMode('voice')}
                      className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                        practiceMode === 'voice' ? 'bg-sage-600 text-white' : 'bg-sage-50 text-sage-700 hover:bg-sage-100'
                      }`}
                    >
                      <Mic className="w-4 h-4" />
                      Voice
                    </button>
                    <button
                      onClick={() => setPracticeMode('text')}
                      className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                        practiceMode === 'text' ? 'bg-sage-600 text-white' : 'bg-sage-50 text-sage-700 hover:bg-sage-100'
                      }`}
                    >
                      <MessageSquare className="w-4 h-4" />
                      Text Chat
                    </button>
                  </div>

                  {isAdmin && practiceMode === 'voice' && (
                    <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <Cpu className="w-4 h-4 text-amber-600" />
                        <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wide">Admin A/B Test — Model Variant</h4>
                      </div>
                      <p className="text-xs text-amber-700 mb-3">Select which OpenAI realtime model to test.</p>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setModelVariant('gpt-realtime-2.1')}
                          className={`px-3 py-2.5 rounded-lg text-xs font-semibold transition-all ${
                            modelVariant === 'gpt-realtime-2.1' ? 'bg-amber-600 text-white ring-2 ring-amber-400' : 'bg-white text-amber-700 border border-amber-200 hover:bg-amber-100'
                          }`}
                        >
                          A: gpt-realtime-2.1
                        </button>
                        <button
                          onClick={() => setModelVariant('gpt-live-1')}
                          className={`px-3 py-2.5 rounded-lg text-xs font-semibold transition-all ${
                            modelVariant === 'gpt-live-1' ? 'bg-amber-600 text-white ring-2 ring-amber-400' : 'bg-white text-amber-700 border border-amber-200 hover:bg-amber-100'
                          }`}
                        >
                          B: gpt-live-1 (5.6-terra)
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={startSession}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-sage-600 text-white rounded-xl font-semibold hover:bg-sage-700 transition-all"
                  >
                    <Sparkles className="w-5 h-5" />
                    Start Milton Model Practice
                  </button>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}

        {step === 'select_style' && selectedModule && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button
              onClick={() => setStep(selectedModule === 'meta_model' && selectedDifficulty ? 'select_difficulty' : 'select_module')}
              className="flex items-center gap-2 text-chocolate-500 hover:text-sage-600 mb-4 transition-colors text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <h2 className="text-lg font-bold text-chocolate-900 mb-2">Choose your practice style</h2>
            <p className="text-sm text-chocolate-500 mb-6">How would you like to practice?</p>
            <div className="grid md:grid-cols-2 gap-3">
              {NLP_PRACTICE_STYLES.map((style) => (
                <button
                  key={style.id}
                  onClick={() => {
                    setSelectedStyle(style.id);
                  }}
                  className={`freud-card-elevated p-5 text-left transition-all ${
                    selectedStyle === style.id ? 'ring-2 ring-sage-500' : 'hover:ring-2 hover:ring-sage-400'
                  }`}
                >
                  <h3 className="text-base font-bold text-chocolate-900 mb-1">{style.label}</h3>
                  <p className="text-xs text-chocolate-500">{style.description}</p>
                </button>
              ))}
            </div>

            {selectedStyle && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 freud-card-elevated p-5"
              >
                <h3 className="text-sm font-bold text-chocolate-900 mb-3">Choose your practice mode</h3>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <button
                    onClick={() => setPracticeMode('voice')}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                      practiceMode === 'voice' ? 'bg-sage-600 text-white' : 'bg-sage-50 text-sage-700 hover:bg-sage-100'
                    }`}
                  >
                    <Mic className="w-4 h-4" />
                    Voice
                  </button>
                  <button
                    onClick={() => setPracticeMode('text')}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                      practiceMode === 'text' ? 'bg-sage-600 text-white' : 'bg-sage-50 text-sage-700 hover:bg-sage-100'
                    }`}
                  >
                    <MessageSquare className="w-4 h-4" />
                    Text Chat
                  </button>
                </div>

                {isAdmin && practiceMode === 'voice' && (
                  <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Cpu className="w-4 h-4 text-amber-600" />
                      <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wide">Admin A/B Test — Model Variant</h4>
                    </div>
                    <p className="text-xs text-amber-700 mb-3">Select which OpenAI realtime model to test. Students do not see this.</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setModelVariant('gpt-realtime-2.1')}
                        className={`px-3 py-2.5 rounded-lg text-xs font-semibold transition-all ${
                          modelVariant === 'gpt-realtime-2.1' ? 'bg-amber-600 text-white ring-2 ring-amber-400' : 'bg-white text-amber-700 border border-amber-200 hover:bg-amber-100'
                        }`}
                      >
                        A: gpt-realtime-2.1
                      </button>
                      <button
                        onClick={() => setModelVariant('gpt-live-1')}
                        className={`px-3 py-2.5 rounded-lg text-xs font-semibold transition-all ${
                          modelVariant === 'gpt-live-1' ? 'bg-amber-600 text-white ring-2 ring-amber-400' : 'bg-white text-amber-700 border border-amber-200 hover:bg-amber-100'
                        }`}
                      >
                        B: gpt-live-1 (5.6-terra)
                      </button>
                    </div>
                  </div>
                )}

                <button
                  onClick={startSession}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-sage-600 text-white rounded-xl font-semibold hover:bg-sage-700 transition-all"
                >
                  <Sparkles className="w-5 h-5" />
                  Start Practice Session
                </button>
              </motion.div>
            )}
          </motion.div>
        )}

        {step === 'practice' && selectedModule && selectedStyle && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => handleEndSession()}
                className="flex items-center gap-2 text-chocolate-500 hover:text-warm-600 transition-colors text-sm font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                End Session
              </button>
              <div className="flex items-center gap-2 text-xs text-chocolate-400">
                <span className="px-2 py-1 bg-sage-50 text-sage-700 rounded-lg font-medium">
                  {selectedModule === 'meta_model' ? 'Meta Model' : selectedModule === 'presuppositions' ? 'Presuppositions' : selectedModule === 'milton_model' ? 'Milton Model' : 'NLP Trainer AI'}
                </span>
                <span className="px-2 py-1 bg-sage-50 text-sage-700 rounded-lg font-medium">{selectedStyle}</span>
                {selectedDifficulty && (
                  <span className="px-2 py-1 bg-sage-50 text-sage-700 rounded-lg font-medium">{selectedDifficulty}</span>
                )}
              </div>
            </div>

            {practiceMode === 'voice' && (
              <div className="freud-card-elevated p-6 mb-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
                    <Volume2 className="w-5 h-5 text-sage-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-chocolate-900">Voice Practice</h3>
                    <p className="text-xs text-chocolate-500">Click below to start your voice session with the AI coach.</p>
                  </div>
                </div>
                <button
                  onClick={() => setVoiceOpen(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-sage-600 text-white rounded-xl font-semibold hover:bg-sage-700 transition-all"
                >
                  <Mic className="w-5 h-5" />
                  Start Voice Session
                </button>
                <p className="text-xs text-chocolate-400 mt-3">
                  When you're done, click "End Session" above to save your practice report.
                </p>
              </div>
            )}

            {practiceMode === 'text' && (
              <div className="freud-card-elevated overflow-hidden flex flex-col">
                <div className="px-6 py-4 border-b border-cream-200 flex items-center gap-2.5">
                  <Brain className="w-5 h-5 text-sage-600" />
                  <h2 className="text-lg font-bold text-chocolate-900">Practice Session</h2>
                </div>

                {error && (
                  <div className="mx-6 mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                <div className="px-6 py-4 overflow-y-auto max-h-[60vh] space-y-4">
                  {messages.length === 0 && !isSending && (
                    <div className="py-12 text-center">
                      <Sparkles className="w-12 h-12 text-cream-400 mx-auto mb-4" />
                      <p className="text-chocolate-500 mb-2">Start your practice session</p>
                      <p className="text-sm text-chocolate-400 max-w-md mx-auto">
                        Type a message to begin practicing {selectedModule === 'meta_model' ? 'Meta Model' : selectedModule === 'presuppositions' ? 'Presuppositions' : selectedModule === 'milton_model' ? 'Milton Model' : 'NLP Trainer AI'} in {selectedStyle} mode.
                      </p>
                    </div>
                  )}
                  {messages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[90%] rounded-2xl px-5 py-3 ${
                        msg.role === 'user'
                          ? 'bg-sage-600 text-white'
                          : 'bg-cream-50 border border-cream-200'
                      }`}>
                        {msg.role === 'assistant' ? (
                          <MarkdownRenderer content={msg.content} />
                        ) : (
                          <p className="text-sm">{msg.content}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {isSending && (
                    <div className="flex justify-start">
                      <div className="bg-cream-50 border border-cream-200 rounded-2xl px-5 py-4">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 bg-sage-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-sage-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-sage-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="px-6 py-4 border-t border-cream-200">
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      placeholder="Type your message..."
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                      disabled={isSending}
                      className="freud-input"
                    />
                    <button
                      onClick={handleSend}
                      disabled={isSending || !chatInput.trim()}
                      className="p-3 bg-sage-600 text-white rounded-xl hover:bg-sage-700 transition-all disabled:opacity-50 flex-shrink-0"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => handleEndSession()}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-warm-600 text-white rounded-xl text-sm font-semibold hover:bg-warm-700 transition-all"
              >
                <RefreshCw className="w-4 h-4" />
                End & Save Session
              </button>
            </div>
          </motion.div>
        )}

        {step === 'trainer_chat' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => handleEndTrainerChat()}
                className="flex items-center gap-2 text-chocolate-500 hover:text-warm-600 transition-colors text-sm font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Modules
              </button>
              <div className="flex items-center gap-2 text-xs text-chocolate-400">
                <span className="px-2 py-1 bg-sage-50 text-sage-700 rounded-lg font-medium">NLP Trainer AI</span>
              </div>
            </div>

            <div className="freud-card-elevated overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-cream-200 flex items-center gap-2.5">
                <GraduationCap className="w-5 h-5 text-sage-600" />
                <div>
                  <h2 className="text-lg font-bold text-chocolate-900">NLP Trainer AI</h2>
                  <p className="text-xs text-chocolate-500">Ask questions about NLP, Timeline Therapy, Hypnosis, and NLP Coaching</p>
                </div>
              </div>

              {error && (
                <div className="mx-6 mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              <div className="px-6 py-4 overflow-y-auto max-h-[55vh] space-y-4">
                {messages.length === 0 && !isSending && (
                  <div className="py-12 text-center">
                    <GraduationCap className="w-12 h-12 text-cream-400 mx-auto mb-4" />
                    <p className="text-chocolate-500 mb-2 font-medium">Ask the NLP Trainer AI</p>
                    <p className="text-sm text-chocolate-400 max-w-md mx-auto mb-6">
                      I'm here to answer your questions about NLP, Timeline Therapy, Hypnosis, and NLP Coaching. What would you like to know?
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {['What is the Meta Model?', 'How do I use anchoring in coaching?', 'Explain Timeline Therapy', 'What are Milton Model patterns?'].map((q) => (
                        <button
                          key={q}
                          onClick={() => {
                            setChatInput(q);
                            setTimeout(() => {
                              const event = new KeyboardEvent('keydown', { key: 'Enter' });
                              document.dispatchEvent(event);
                            }, 100);
                          }}
                          className="px-3 py-1.5 bg-sage-50 text-sage-700 rounded-lg text-xs font-medium hover:bg-sage-100 transition-colors"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[90%] rounded-2xl px-5 py-3 ${
                      msg.role === 'user'
                        ? 'bg-sage-600 text-white'
                        : 'bg-cream-50 border border-cream-200'
                    }`}>
                      {msg.role === 'assistant' ? (
                        <MarkdownRenderer content={msg.content} />
                      ) : (
                        <p className="text-sm">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                {isSending && (
                  <div className="flex justify-start">
                    <div className="bg-cream-50 border border-cream-200 rounded-2xl px-5 py-4">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 bg-sage-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-sage-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-sage-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="px-6 py-4 border-t border-cream-200">
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    placeholder="Ask a question about NLP, Timeline Therapy, Hypnosis..."
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendTrainer()}
                    disabled={isSending}
                    className="freud-input"
                  />
                  <button
                    onClick={handleSendTrainer}
                    disabled={isSending || !chatInput.trim()}
                    className="p-3 bg-sage-600 text-white rounded-xl hover:bg-sage-700 transition-all disabled:opacity-50 flex-shrink-0"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => handleEndTrainerChat()}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-warm-600 text-white rounded-xl text-sm font-semibold hover:bg-warm-700 transition-all"
              >
                <RefreshCw className="w-4 h-4" />
                End Session
              </button>
            </div>
          </motion.div>
        )}

        {step === 'history' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button
              onClick={() => setStep('select_module')}
              className="flex items-center gap-2 text-chocolate-500 hover:text-sage-600 mb-4 transition-colors text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Practice
            </button>
            <h2 className="text-lg font-bold text-chocolate-900 mb-4">Practice History</h2>
            {sessions.length === 0 ? (
              <div className="freud-card-elevated p-8 text-center">
                <BookOpen className="w-12 h-12 text-cream-400 mx-auto mb-3" />
                <p className="text-chocolate-500">No practice sessions yet. Start your first session!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sessions.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => handleViewSession(s)}
                    className="freud-card-elevated p-5 cursor-pointer hover:ring-2 hover:ring-sage-300 transition-all group"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
                          <span className="text-sm font-bold text-sage-700">
                            {s.module === 'meta_model' ? 'MM' : s.module === 'presuppositions' ? 'PS' : s.module === 'milton_model' ? 'ML' : 'NT'}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-chocolate-900">
                            {s.module === 'meta_model' ? 'Meta Model' : s.module === 'presuppositions' ? 'Presuppositions' : s.module === 'milton_model' ? 'Milton Model' : 'NLP Trainer AI'} — {s.practice_style}
                          </p>
                          <p className="text-xs text-chocolate-400">
                            {new Date(s.created_at).toLocaleString()} · {s.mode === 'voice' ? 'Voice' : 'Text'} · {Math.floor(s.duration_seconds / 60)}m {s.duration_seconds % 60}s
                          </p>
                          {s.difficulty && (
                            <p className="text-xs text-sage-600 font-medium capitalize">{s.difficulty}</p>
                          )}
                          {s.status === 'in_progress' && (
                            <p className="text-xs text-amber-600 font-medium mt-0.5">In progress — not yet completed</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {s.score !== null && (
                          <div className={`px-3 py-1.5 rounded-lg text-sm font-bold ${
                            s.score >= 80 ? 'bg-sage-100 text-sage-700' :
                            s.score >= 60 ? 'bg-amber-100 text-amber-700' :
                            'bg-warm-100 text-warm-700'
                          }`}>
                            {s.score}{s.module === 'milton_model' ? '/10' : '/100'}
                          </div>
                        )}
                        {s.status === 'in_progress' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleResumeSession(s); }}
                            className="inline-flex items-center gap-1.5 px-3 py-2 bg-sage-50 text-sage-700 rounded-lg text-xs font-semibold hover:bg-sage-100 transition-colors"
                            title="Resume practice session"
                          >
                            <Play className="w-3.5 h-3.5" />
                            Resume
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); setSessionToDelete(s); }}
                          className="p-2 text-chocolate-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all"
                          title="Delete session"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {s.feedback_summary && (
                      <div className="mt-3 pt-3 border-t border-cream-200">
                        <MarkdownRenderer content={s.feedback_summary} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
        {step === 'view_session' && viewingSession && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button
              onClick={() => { setStep('history'); setViewingSession(null); setViewingMessages([]); }}
              className="flex items-center gap-2 text-chocolate-500 hover:text-sage-600 mb-4 transition-colors text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to History
            </button>
            <div className="freud-card-elevated overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-cream-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
                    <span className="text-sm font-bold text-sage-700">
                      {viewingSession.module === 'meta_model' ? 'MM' : viewingSession.module === 'presuppositions' ? 'PS' : viewingSession.module === 'milton_model' ? 'ML' : 'NT'}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-chocolate-900">
                      {viewingSession.module === 'meta_model' ? 'Meta Model' : viewingSession.module === 'presuppositions' ? 'Presuppositions' : viewingSession.module === 'milton_model' ? 'Milton Model' : 'NLP Trainer AI'} — {viewingSession.practice_style}
                    </h2>
                    <p className="text-xs text-chocolate-400">
                      {new Date(viewingSession.created_at).toLocaleString()} · {viewingSession.mode === 'voice' ? 'Voice' : 'Text'} · {Math.floor(viewingSession.duration_seconds / 60)}m {viewingSession.duration_seconds % 60}s
                      {viewingSession.status === 'in_progress' && ' · In progress'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {viewingSession.score !== null && (
                    <div className={`px-3 py-1.5 rounded-lg text-sm font-bold ${
                      viewingSession.score >= 80 ? 'bg-sage-100 text-sage-700' :
                      viewingSession.score >= 60 ? 'bg-amber-100 text-amber-700' :
                      'bg-warm-100 text-warm-700'
                    }`}>
                      {viewingSession.score}{viewingSession.module === 'milton_model' ? '/10' : '/100'}
                    </div>
                  )}
                  {viewingSession.status === 'in_progress' && (
                    <button
                      onClick={() => handleResumeSession(viewingSession)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-sage-600 text-white rounded-xl text-sm font-semibold hover:bg-sage-700 transition-all"
                    >
                      <Play className="w-3.5 h-3.5" />
                      Resume
                    </button>
                  )}
                  <button
                    onClick={() => setSessionToDelete(viewingSession)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-warm-50 text-warm-700 rounded-xl text-sm font-semibold hover:bg-warm-100 transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                </div>
              </div>

              {viewingSession.feedback_summary && (
                <div className="px-6 py-3 bg-cream-50 border-b border-cream-200">
                  <p className="text-xs font-bold text-chocolate-700 mb-1 uppercase tracking-wide">Practice Report</p>
                  <MarkdownRenderer content={viewingSession.feedback_summary} />
                </div>
              )}

              <div className="px-6 py-4 overflow-y-auto max-h-[55vh] space-y-4">
                {isLoadingMessages ? (
                  <div className="py-12 text-center">
                    <LoadingSpinner message="Loading conversation..." />
                  </div>
                ) : viewingMessages.length === 0 ? (
                  <div className="py-12 text-center">
                    <MessageSquare className="w-12 h-12 text-cream-400 mx-auto mb-3" />
                    <p className="text-chocolate-500">No messages were saved in this session.</p>
                    {viewingSession.mode === 'voice' && (
                      <p className="text-sm text-chocolate-400 mt-1">Voice sessions do not save text transcripts.</p>
                    )}
                  </div>
                ) : (
                  viewingMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[90%] rounded-2xl px-5 py-3 ${
                        msg.role === 'user'
                          ? 'bg-sage-600 text-white'
                          : 'bg-cream-50 border border-cream-200'
                      }`}>
                        {msg.role === 'assistant' ? (
                          <MarkdownRenderer content={msg.content} />
                        ) : (
                          <p className="text-sm">{msg.content}</p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        )}

        {sessionToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => !isDeleting && setSessionToDelete(null)}>
            <div className="freud-card-elevated p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-chocolate-900">Delete Session?</h3>
              </div>
              <p className="text-sm text-chocolate-500 mb-5">
                This will permanently delete this practice session{sessionToDelete.status === 'in_progress' ? ' (which is in progress)' : ''} and its conversation. This cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setSessionToDelete(null)}
                  disabled={isDeleting}
                  className="px-4 py-2.5 bg-cream-50 text-chocolate-600 rounded-xl text-sm font-semibold hover:bg-cream-100 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteSession}
                  disabled={isDeleting}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      <NLPPracticeVoice
        isOpen={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        module={selectedModule || 'meta_model'}
        practiceStyle={selectedStyle || 'learn'}
        difficulty={selectedDifficulty}
        modelVariant={isAdmin ? modelVariant : undefined}
        miltonOptions={selectedModule === 'milton_model' ? {
          patternFocus: miltonPatternFocus,
          practiceContext: miltonContext,
          feedbackTiming: miltonFeedbackTiming,
          targetDurationMinutes: miltonDuration,
        } : null}
        onSessionEnd={(duration, usageLogId) => {
          if (usageLogId && duration > 0) {
            supabase.rpc('update_nlp_practice_voice_duration', {
              p_usage_log_id: usageLogId,
              p_duration_seconds: duration,
            }).then(({ error }) => {
              if (error) console.error('Failed to log voice duration:', error.message);
            });
          }
        }}
      />
    </div>
  );
}
